import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, BookOpen, Brain, Flame, Target, Briefcase, GraduationCap, AlertCircle, ChevronRight, Sparkles, Trophy, Zap, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  XAxis, YAxis, Tooltip, ResponsiveContainer, 
  Radar, RadarChart, PolarGrid, PolarAngleAxis, CartesianGrid, AreaChart, Area
} from 'recharts';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useProgress } from '../context/ProgressContext';
import toast from 'react-hot-toast';

const Account = () => {
  const { user } = useAuth();
  const { solvedQuestions, aptitudeSolved, resetProgress } = useProgress();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      const statsRes = await api.getStats(user.id);
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Data fetch failed', err);
      toast.error('Failed to load your stats.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const handleResetProgress = async () => {
    if (window.confirm('Are you sure you want to reset all your progress? This action cannot be undone.')) {
      const toastId = toast.loading('Resetting your progress...');
      try {
        const res = await api.resetProgress();
        if (res.ok) {
          resetProgress(); // Clear local state
          toast.success('Your progress has been successfully reset.', { id: toastId });
          // Refetch stats to update the UI
          fetchStats();
        } else {
          const data = await res.json();
          toast.error(data.message || 'Failed to reset progress.', { id: toastId });
        }
      } catch (err) {
        toast.error('An unexpected error occurred. Please try again.', { id: toastId });
        console.error('Reset progress error:', err);
      }
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center max-w-md mx-auto animate-fade-in-up">
        <div className="relative mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-indigo-500/30 rotate-6">
            <TrendingUp className="w-12 h-12 text-white -rotate-6" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center shadow-lg rotate-12">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4 font-display">Track Your Progress</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
          Login to see your solved questions, aptitude scores, streak data, and stay motivated on your placement journey.
        </p>
        <Link to="/auth" className="btn-primary w-full flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" />
          Login to My Account
        </Link>
      </div>
    );
  }

  const dsaSolved = stats ? stats.totalDsa : (solvedQuestions?.size || 0);
  const aptSolved = stats ? stats.totalAptitude : (aptitudeSolved?.size || 0);
  const dsaProgressPercent = Math.min(Math.round((dsaSolved / 200) * 100), 100);
  const aptitudeProgressPercent = Math.min(Math.round((aptSolved / 200) * 100), 100);
  const totalSolved = dsaSolved + aptSolved;

  // Level system
  const getLevel = (total) => {
    if (total >= 300) return { name: 'Platinum', icon: '💎', color: 'from-cyan-400 to-blue-500', textColor: 'text-cyan-500' };
    if (total >= 200) return { name: 'Gold', icon: '🏆', color: 'from-amber-400 to-yellow-500', textColor: 'text-amber-500' };
    if (total >= 100) return { name: 'Silver', icon: '🥈', color: 'from-gray-300 to-gray-400', textColor: 'text-gray-400' };
    if (total >= 30) return { name: 'Bronze', icon: '🥉', color: 'from-orange-400 to-amber-600', textColor: 'text-orange-500' };
    return { name: 'Starter', icon: '🌱', color: 'from-emerald-400 to-green-500', textColor: 'text-emerald-500' };
  };
  const level = getLevel(totalSolved);

  // Chart Data
  const dailyData = stats?.dailyActivity?.map(d => ({
    name: d.date.split('-').slice(1).join('/'), 
    solved: d.count
  })) || [];

  const radarData = stats?.topicProgress?.map(tp => ({
    subject: tp.topic,
    A: tp.solvedCount,
    fullMark: 20
  })) || [];

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-12">
      {/* ═══ Profile Header ═══ */}
      <div className="glass-card p-6 md:p-8 relative overflow-hidden">
        {/* Decorative gradient */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-gradient-to-br from-indigo-500/5 to-purple-500/5 blur-3xl pointer-events-none" />

        <div className="relative flex flex-col md:flex-row items-center md:items-start gap-8">
          {/* Avatar */}
          <div className="relative group">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-3xl text-white font-extrabold shadow-xl shadow-indigo-500/20 rotate-3 group-hover:rotate-0 transition-transform duration-300">
              <span className="-rotate-3 group-hover:rotate-0 transition-transform duration-300 font-display">{user.name.charAt(0).toUpperCase()}</span>
            </div>
            {/* Level badge */}
            <div className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-lg bg-gradient-to-br ${level.color} flex items-center justify-center text-sm shadow-lg border-2 border-white dark:border-gray-900`}>
              {level.icon}
            </div>
          </div>

          <div className="text-center md:text-left flex-1 space-y-3">
            <div>
              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white font-display">{user.name}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
            </div>
            
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              <Badge icon={<GraduationCap className="w-3.5 h-3.5" />} text={user.branch || "Not Set"} color="indigo" />
              <Badge icon={<Briefcase className="w-3.5 h-3.5" />} text={`Target: ${user.target_company || "Any"}`} color="orange" />
              <Badge icon={<Target className="w-3.5 h-3.5" />} text={user.skill_level || "Beginner"} color="green" />
              <Badge icon={<Trophy className="w-3.5 h-3.5" />} text={`${level.name} Level`} color="purple" />
            </div>
          </div>

          {/* Streak */}
          <div className="glass-card p-5 rounded-2xl flex flex-col items-center min-w-[120px]">
            <div className="relative">
              <Flame className={`w-10 h-10 ${stats?.streak > 0 ? "text-orange-500 fill-orange-500" : "text-gray-300 dark:text-gray-600"}`} />
              {stats?.streak > 0 && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500" />
                </span>
              )}
            </div>
            <p className="text-2xl font-extrabold text-gray-900 dark:text-white mt-2 font-display">{stats?.streak || 0}</p>
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Day Streak</p>
          </div>
        </div>
      </div>

      {/* ═══ Quick Stats ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
        <QuickStat label="DSA Solved" value={dsaSolved} icon={<BookOpen className="w-5 h-5" />} color="indigo" />
        <QuickStat label="Aptitude Done" value={aptSolved} icon={<Brain className="w-5 h-5" />} color="purple" />
        <QuickStat label="Total Progress" value={totalSolved} icon={<Zap className="w-5 h-5" />} color="emerald" />
        <QuickStat label="Level" value={level.name} icon={<Star className="w-5 h-5" />} color="amber" isText />
      </div>

      {/* ═══ Progress Rings ═══ */}
      <div className="grid md:grid-cols-2 gap-6">
        <ProgressCard 
          title="DSA Progress" 
          percent={dsaProgressPercent} 
          count={dsaSolved} 
          total={200} 
          gradientId="dsa-ring"
          gradientColors={['#6366f1', '#a78bfa']}
          icon={<BookOpen className="w-5 h-5" />}
        />
        <ProgressCard 
          title="Aptitude Mastery" 
          percent={aptitudeProgressPercent} 
          count={aptSolved} 
          total={200} 
          gradientId="apt-ring"
          gradientColors={['#a855f7', '#ec4899']}
          icon={<Brain className="w-5 h-5" />}
        />
      </div>

      {/* ═══ Danger Zone ═══ */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-bold text-red-500 mb-4 flex items-center gap-3 font-display">
          <AlertCircle className="w-5 h-5" />
          Danger Zone
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          This is a permanent action. Once you reset your progress, it cannot be recovered.
        </p>
        <button
          onClick={handleResetProgress}
          className="btn-danger"
        >
          Reset All My Progress
        </button>
      </div>

      {/* ═══ Charts ═══ */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Consistency Graph */}
        <div className="glass-card p-6 min-h-[350px]">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3 font-display">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            Consistency Tracker
          </h3>
          <div className="h-[250px] w-full">
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData}>
                  <defs>
                    <linearGradient id="colorSolved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#88888815" vertical={false} />
                  <XAxis dataKey="name" stroke="#888" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#888" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1f2937', 
                      border: 'none', 
                      borderRadius: '12px', 
                      color: '#fff',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                      padding: '12px 16px'
                    }}
                    itemStyle={{ color: '#818cf8' }}
                  />
                  <Area type="monotone" dataKey="solved" stroke="#6366f1" strokeWidth={3} fill="url(#colorSolved)" dot={{ r: 4, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6, stroke: '#6366f1', strokeWidth: 2, fill: '#fff' }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart icon={<BookOpen className="w-8 h-8" />} text="Start solving to see your consistency graph!" />
            )}
          </div>
        </div>

        {/* Mastery Radar */}
        <div className="glass-card p-6 min-h-[350px]">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3 font-display">
            <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
              <Brain className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            Skill Mastery Map
          </h3>
          <div className="h-[250px] w-full">
            {radarData.length >= 3 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                  <PolarGrid stroke="#88888830" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: '#888', fontSize: 10 }} />
                  <Radar name="Solved" dataKey="A" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart icon={<Target className="w-8 h-8" />} text="Solve at least 3 topics to unlock your Skill Radar!" />
            )}
          </div>
        </div>
      </div>

      {/* ═══ Topics & Recommendations ═══ */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Topic Breakdown */}
        <div className="lg:col-span-2">
          {!loading && stats?.topicProgress?.length > 0 && (
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 font-display">Topic Breakdown</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 stagger-children">
                {stats.topicProgress.slice(0, 6).map((tp, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700 hover:border-indigo-400/50 dark:hover:border-indigo-500/30 transition-all duration-300 group hover:-translate-y-0.5">
                    <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">{tp.topic}</p>
                    <p className="text-xl font-extrabold text-gray-900 dark:text-white font-display">
                      {tp.solvedCount}
                      <span className="text-xs font-normal text-gray-400 ml-1">solved</span>
                    </p>
                    {/* Mini progress bar */}
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 h-1 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-1000" style={{ width: `${Math.min((tp.solvedCount / 20) * 100, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Needs Work & Suggestions */}
        <div className="space-y-6">
          <div className="glass-card p-6 border-l-4 border-l-amber-500">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3 font-display">
              <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              Needs Work
            </h3>
            <div className="space-y-3">
              {stats?.weakAreas?.length > 0 ? stats.weakAreas.map((area, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/20 rounded-xl border border-amber-100 dark:border-amber-900/30 group hover:border-amber-300 dark:hover:border-amber-700 transition-colors">
                  <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">{area}</span>
                  <Link to="/dsa" className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-amber-200 dark:hover:bg-amber-900/40 transition">
                    <ChevronRight className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </Link>
                </div>
              )) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Solve more to identify weak areas...</p>
              )}
            </div>
            
            {stats?.suggestions?.length > 0 && (
              <div className="mt-6 pt-5 border-t border-gray-100 dark:border-gray-700">
                <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Recommended Next</h4>
                <div className="space-y-2">
                  {stats.suggestions.map((sug, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex-shrink-0" />
                      {sug}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Sub-Components ─── */

const Badge = ({ icon, text, color }) => {
  const colors = {
    indigo: "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-800/50",
    orange: "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-800/50",
    green: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/50",
    purple: "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800/50",
  };
  return (
    <div className={`flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg border ${colors[color]}`}>
      <span className="opacity-70">{icon}</span>
      {text}
    </div>
  );
};

const QuickStat = ({ label, value, icon, color, isText }) => {
  const colorMap = {
    indigo: 'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40',
    purple: 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/40',
    emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40',
    amber: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40',
  };
  return (
    <div className="glass-card p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-transform duration-300">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorMap[color]}`}>
        {icon}
      </div>
      <div>
        <p className={`${isText ? 'text-lg' : 'text-2xl'} font-extrabold text-gray-900 dark:text-white font-display`}>{value}</p>
        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
    </div>
  );
};

const ProgressCard = ({ title, percent, count, total, gradientId, gradientColors, icon }) => {
  const r = 60;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - percent / 100);

  return (
    <div className="glass-card p-6 flex items-center gap-8">
      {/* SVG Progress Ring */}
      <div className="relative w-[140px] h-[140px] flex-shrink-0">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 140 140">
          <circle cx="70" cy="70" r={r} fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-100 dark:text-gray-800" />
          <circle
            cx="70" cy="70" r={r} fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="progress-ring-circle"
          />
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={gradientColors[0]} />
              <stop offset="100%" stopColor={gradientColors[1]} />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-3xl font-extrabold text-gray-900 dark:text-white font-display">{percent}%</p>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{count}/{total}</p>
        </div>
      </div>

      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-indigo-600 dark:text-indigo-400">{icon}</span>
          <h4 className="text-lg font-bold text-gray-900 dark:text-white font-display">{title}</h4>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {percent < 25 ? 'Just getting started — keep going!' : 
           percent < 50 ? 'Good progress! Consistency is key.' :
           percent < 75 ? 'Over halfway! You\'re building mastery.' :
           'Almost there! Outstanding work!'}
        </p>
        <div className="w-full bg-gray-100 dark:bg-gray-800 h-2 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{ 
              width: `${percent}%`,
              background: `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})` 
            }} 
          />
        </div>
      </div>
    </div>
  );
};

const EmptyChart = ({ icon, text }) => (
  <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 space-y-3">
    <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
      {React.cloneElement(icon, { className: 'w-7 h-7 opacity-40' })}
    </div>
    <p className="text-sm text-center max-w-xs">{text}</p>
  </div>
);

export default Account;
